import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Skeleton from 'react-loading-skeleton';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';


const CfdiRelacionados = ({ register, setValue, watch }) => {
    const { token } = useToken();
    const [tiposRelacion, setTiposRelacion] = useState([]);
    const [cargandoTipos, setCargandoTipos] = useState(true);
    const [uuids, setUuids] = useState([]);
    const [nuevoUuid, setNuevoUuid] = useState('');
    const [uuidInfo, setUuidInfo] = useState({});
    const { showErrorToast } = useToast();

    const tipoSeleccionado = watch("cfdiRelacionados.TipoRelacion");

    useEffect(() => {
        const fetchTiposRelacion = async () => {
            try {
                const response = await fetch('/api/contabilidad/CatalogoSAT/ObtenerTipoRelacion', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const resJson = await response.json();
                    if (resJson.iResponseType === 1) {
                        try {
                            const objDetalles = JSON.parse(resJson.detalles);
                            // La estructura típica del backend es colocar los catálogos en Table1 o table1
                            const listaTipos = objDetalles.Table1 || objDetalles.table1 || objDetalles.table2 || [];
                            setTiposRelacion(listaTipos);
                        } catch (e) {
                            console.error('Error al procesar detalles de tipos de relación:', e);
                            setTiposRelacion([]);
                        }
                    } else {
                        console.error('Error al obtener tipos de relación:', resJson.vMessage);
                    }
                }
            } catch (error) {
                console.error('Error de red al obtener tipos de relación:', error);
            } finally {
                setCargandoTipos(false);
            }
        };

        if (token) {
            fetchTiposRelacion();
        }
    }, [token]);

    // Sincronizar UUIDs con react-hook-form
    useEffect(() => {
        register('cfdiRelacionados.UUIDs');
    }, [register]);

    useEffect(() => {
        if (uuids.length > 0 && tipoSeleccionado) {
            setValue('cfdiRelacionados.UUIDs', uuids.map(u => ({ UUID: u })));
        } else {
            setValue('cfdiRelacionados.UUIDs', null);
        }
    }, [uuids, tipoSeleccionado, setValue]);

    const fetchUuidInfo = async (uuid) => {
        try {
            const response = await fetch(`/api/contabilidad/Facturacion/ObtenerInfoRelacionadaPorUUID?uuid=${uuid}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const resJson = await response.json();
                if (resJson.iResponseType === 1 && resJson.data) {
                    setUuidInfo(prev => ({ ...prev, [uuid]: resJson.data }));
                }
            }
        } catch (error) {
            console.error('Error al obtener info del UUID relacionado:', error);
        }
    };

    const handleAddUuid = () => {
        const uuid = nuevoUuid.trim().toUpperCase();
        if (!uuid) return;

        // Validar formato UUID (8-4-4-4-12)
        const uuidRegex = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
        if (!uuidRegex.test(uuid)) {
            showErrorToast('El formato del Folio Fiscal (UUID) no es válido.');
            return;
        }

        if (!uuids.includes(uuid)) {
            setUuids([...uuids, uuid]);
            setNuevoUuid('');
            fetchUuidInfo(uuid);
        }
    };

    const handleRemoveUuid = (uuidToRemove) => {
        setUuids(uuids.filter(u => u !== uuidToRemove));
    };

    return (
        <Card className="factura-card mb-1 bg-white">
            <Card.Body className="factura-card-body">
                <h5 className="seccion-titulo mt-0 mb-3">
                    <FontAwesomeIcon icon="link" className="me-2 text-primary" />
                    CFDI Relacionados
                </h5>
                <Row>
                    <Col md={12} className="mb-3">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">Tipo de Relación</Form.Label>
                            {cargandoTipos ? (
                                <Skeleton />
                            ) : (
                                <Form.Select
                                    className="form-control-profesional"
                                    size="sm"
                                    {...register("cfdiRelacionados.TipoRelacion")}
                                >
                                    <option value="">(Ninguno) - No relacionar CFDI</option>
                                    {tiposRelacion.map((tipo, idx) => (
                                        <option key={idx} value={tipo.vClave}>
                                            {tipo.vClave} - {tipo.vDescripcion}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                {tipoSeleccionado && (
                    <Row>
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label className="form-label-profesional">Agregar Folio Fiscal (UUID)</Form.Label>
                                <InputGroup size="sm">
                                    <Form.Control
                                        className="form-control-profesional"
                                        placeholder="Ej. 560A8451-A29C-41D4-A716-544676554400"
                                        value={nuevoUuid}
                                        onChange={(e) => setNuevoUuid(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddUuid();
                                            }
                                        }}
                                        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                    />
                                    <Button
                                        variant="primary"
                                        onClick={handleAddUuid}
                                        style={{ borderTopRightRadius: '6px', borderBottomRightRadius: '6px' }}
                                    >
                                        Agregar
                                    </Button>
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        {uuids.length > 0 && (
                            <Col md={12}>
                                <div className="p-3 bg-light rounded-3 border">
                                    <strong className="d-block text-dark mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        UUIDs Relacionados
                                    </strong>
                                    {uuids.map((uuid, idx) => {
                                        const info = uuidInfo[uuid];
                                        return (
                                            <div key={idx} className="bg-white border rounded p-3 mb-2 shadow-sm position-relative">
                                                <div className="d-flex align-items-start justify-content-between mb-2">
                                                    <div>
                                                        <span className="text-primary fw-bold" style={{ fontSize: '0.8rem' }}>CFDI RELACIONADO</span>
                                                        <br />
                                                        <span className="text-muted fw-medium" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{uuid}</span>
                                                    </div>
                                                    <Button variant="outline-danger" size="sm" className="border-0" onClick={() => handleRemoveUuid(uuid)} style={{ position: 'absolute', top: '10px', right: '10px' }}>
                                                        <FontAwesomeIcon icon="trash" />
                                                    </Button>
                                                </div>
                                                {info ? (
                                                    <Row className="g-3 mt-2 border-top pt-2">
                                                        <Col xs={12} sm={6} md={3}>
                                                            <span className="text-muted d-block fw-bold text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Receptor</span>
                                                            <div className="text-dark fw-medium text-truncate" style={{ fontSize: '0.8rem' }} title={info.vRazonSocialReceptor}>
                                                                {info.vRazonSocialReceptor}
                                                            </div>
                                                        </Col>
                                                        <Col xs={12} sm={6} md={2}>
                                                            <span className="text-muted d-block fw-bold text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>RFC</span>
                                                            <div className="text-dark fw-medium" style={{ fontSize: '0.8rem' }}>
                                                                {info.vRfcReceptor}
                                                            </div>
                                                        </Col>
                                                        <Col xs={6} sm={4} md={2}>
                                                            <span className="text-muted d-block fw-bold text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Total</span>
                                                            <div className="text-dark fw-bold" style={{ fontSize: '0.8rem' }}>
                                                                {info.vMoneda} ${info.dTotal}
                                                            </div>
                                                        </Col>
                                                        <Col xs={6} sm={4} md={2}>
                                                            <span className="text-muted d-block fw-bold text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Tipo</span>
                                                            <div className="text-dark fw-medium" style={{ fontSize: '0.8rem' }}>
                                                                {info.vTipoComprobante}
                                                            </div>
                                                        </Col>
                                                        <Col xs={6} sm={4} md={1}>
                                                            <span className="text-muted d-block fw-bold text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Método</span>
                                                            <div className="text-dark fw-medium" style={{ fontSize: '0.8rem' }}>
                                                                {info.vMetodoPago}
                                                            </div>
                                                        </Col>
                                                        <Col xs={6} sm={12} md={2}>
                                                            <span className="text-muted d-block fw-bold text-uppercase mb-1" style={{ fontSize: '0.65rem' }}>Forma Pago</span>
                                                            <div className="text-dark fw-medium text-truncate" style={{ fontSize: '0.8rem' }} title={info.vFormaPago}>
                                                                {info.vFormaPago}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                ) : (
                                                    <div className="mt-2 border-top pt-2 text-muted" style={{ fontSize: '0.75rem' }}>
                                                        <i className="bi bi-info-circle me-1"></i> No se encontró información adicional en el panel.
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Col>
                        )}
                    </Row>
                )}
            </Card.Body>
        </Card>
    );
};

CfdiRelacionados.propTypes = {
    register: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    watch: PropTypes.func.isRequired
};

export default CfdiRelacionados;
